import {
  HttpClient,
  HttpContext,
  HttpContextToken,
  HttpErrorResponse,
  HttpStatusCode,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { debugLog } from '../../helpers/debugLog';
import { OfflineBookRegistry, type OutboxOp } from './offline-db';
import { OfflineOutbox } from './offline-outbox';
import { deserializeBody, withoutCreated } from './offline-writes';

/** Requests of the sync itself: the offline interceptor must let them through untouched. */
export const OFFLINE_SYNC_REQUEST = new HttpContextToken<boolean>(() => false);

export interface OfflineSyncResult {
  sent: number;
  rejected: number;
  /** Still queued: no connection, session expired or another tab syncing. */
  remaining: number;
}

type OpOutcome = 'sent' | 'rejected' | 'stop';

/**
 * Sends the writes made offline, in order, when the book is opened again with connection. Each
 * op carries an `Idempotency-Key`, so a retry after a lost response is not applied twice.
 */
@Injectable({
  providedIn: 'root',
})
export class OfflineSyncService {
  private static readonly LOCK_PREFIX = 'nr-offline-sync:';
  private static readonly SERVER_ERROR = 500;
  private static readonly RETRYABLE = new Set<number>([
    0,
    HttpStatusCode.Unauthorized,
    HttpStatusCode.Forbidden,
    HttpStatusCode.RequestTimeout,
    HttpStatusCode.Conflict,
    HttpStatusCode.TooManyRequests,
  ]);

  private readonly http = inject(HttpClient);
  private readonly outbox = inject(OfflineOutbox);
  private readonly registry = inject(OfflineBookRegistry);

  async pendingCount(loanKey: string): Promise<number> {
    return (await this.outbox.pending(loanKey)).length;
  }

  /** Flushes the queue of a loan with a valid token; a single tab does it at a time. */
  async flush(loanKey: string, token: string): Promise<OfflineSyncResult> {
    const run = async (): Promise<OfflineSyncResult> => this.flushQueue(loanKey, token);
    if (typeof navigator.locks === 'undefined') return run();
    let result: OfflineSyncResult = { sent: 0, rejected: 0, remaining: -1 };
    await navigator.locks.request(
      `${OfflineSyncService.LOCK_PREFIX}${loanKey}`,
      { ifAvailable: true },
      async (lock) => {
        // Another tab is already syncing this loan.
        if (lock) result = await run();
      },
    );
    return result;
  }

  private async flushQueue(loanKey: string, token: string): Promise<OfflineSyncResult> {
    const result: OfflineSyncResult = { sent: 0, rejected: 0, remaining: 0 };
    // Re-read after each op: a create rewrites the temporary ids of the following ones.
    for (
      let ops = await this.outbox.pending(loanKey);
      ops.length;
      ops = await this.outbox.pending(loanKey)
    ) {
      const outcome = await this.send(ops[0], token);
      if (outcome === 'stop') {
        result.remaining = ops.length;
        break;
      }
      result[outcome === 'sent' ? 'sent' : 'rejected'] += 1;
    }
    return result;
  }

  private async send(op: OutboxOp, token: string): Promise<OpOutcome> {
    try {
      const text = await firstValueFrom(
        this.http.request(op.method, op.url, {
          body: deserializeBody(op.body),
          headers: { 'token': token, 'Idempotency-Key': op.opId },
          // The token cookie is shared by every tab of the origin and may belong to another book.
          credentials: 'omit',
          context: new HttpContext().set(OFFLINE_SYNC_REQUEST, true),
          responseType: 'text',
        }),
      );
      await this.outbox.complete(op, OfflineSyncService.idOf(text));
      return 'sent';
    } catch (error) {
      const status = error instanceof HttpErrorResponse ? error.status : 0;
      if (OfflineSyncService.RETRYABLE.has(status) || status >= OfflineSyncService.SERVER_ERROR) {
        debugLog('Offline sync stopped, will retry', status);
        return 'stop';
      }
      debugLog('Offline change rejected by the server', status, op.url);
      await this.outbox.fail(op, status);
      if (op.tempId) await this.forgetCreated(op.loanKey, op.tempId);
      return 'rejected';
    }
  }

  /**
   * What the rejected op created offline was never saved: an answer shows as not answered again,
   * also in the next offline open, so the student answers it once more.
   */
  private async forgetCreated(loanKey: string, tempId: string): Promise<void> {
    const snapshot = await this.registry.getSnapshot(loanKey);
    if (snapshot) await this.registry.putSnapshot(withoutCreated(snapshot, tempId));
  }

  private static idOf(text: string): string | undefined {
    try {
      const body: unknown = JSON.parse(text);
      const id: unknown =
        typeof body === 'object' && body !== null ? Reflect.get(body, 'id') : undefined;
      return typeof id === 'string' ? id : undefined;
    } catch {
      return undefined;
    }
  }
}
